/**
 * Casca da aplicacao (spec 0008).
 *
 * Sem router: a pagina vive em `?page=<id>`. Nao ha contrato de URL externo a preservar aqui - o
 * manager e ferramenta interna, ninguem faz deep-link para ele -, mas refletir a pagina na URL faz
 * o F5 e o favorito continuarem funcionando, que e o minimo que se espera de uma ferramenta de
 * trabalho.
 *
 * O cabecalho de cada secao (sobretitulo, titulo e descricao) vive AQUI, e nao em cada pagina: assim
 * ele aparece igual em todos os estados - carregando, com erro ou com dado -, como no Auth Manager.
 */

import type { IconName } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react'

import {
  RESOURCE_CATALOGO,
  RESOURCE_CONEXOES,
  RESOURCE_DIAGNOSTICO,
  RESOURCE_POLICIES,
} from './api/types'
import { AuthGate } from './auth/AuthGate'
import { fetchMe } from './auth/api'
import { usePermissoes } from './auth/permissoes'
import { AuthCatalogPage } from './diagnostico/AuthCatalogPage'
import { ClientesPage } from './entidades/ClientesPage'
import { ConexoesPage } from './entidades/ConexoesPage'
import { DatasetsPage } from './entidades/DatasetsPage'
import { HospitaisPage } from './entidades/HospitaisPage'
import { PoliticasPage } from './entidades/PoliticasPage'
import { RelatoriosPage } from './entidades/RelatoriosPage'
import { HomePage } from './home/HomePage'
import { OnboardingPage } from './onboarding/OnboardingPage'
import { AppShell, NavGroup, NavLink, PageHeader } from './shell/AppShell'
import { ProvedorDeRegistro, useRegistroAberto } from './shell/registro'
import { aplicarTema, lerTema } from './shell/theme'
import './styles/app.css'

interface Secao {
  id: string
  rotulo: string
  titulo: string
  /** Grupo da navegacao, e sobretitulo do cabecalho. */
  grupo: string
  icone: IconName
  descricao: ReactNode
  /** Resource do Auth que a secao le (spec 0009). Sem `read`, ela nao aparece. */
  recurso: string
}

const SECOES: Secao[] = [
  {
    id: 'home',
    rotulo: 'Visão geral',
    titulo: 'Estado do catálogo',
    grupo: 'Visão geral',
    icone: 'chart',
    descricao: 'O catálogo e a publicação em números, e os sinais de que algo precisa de atenção.',
    recurso: RESOURCE_CATALOGO,
  },
  {
    id: 'onboarding',
    rotulo: 'Onboarding',
    titulo: 'Onboarding guiado',
    grupo: 'Visão geral',
    icone: 'workflow',
    descricao: (
      <>
        Siga os passos em ordem: cada um depende do anterior. Identidade — tenant, organizations,
        workspaces e profiles — vem do <strong>Auth</strong> e não é criada aqui; nesta tela ela é
        mapeada, e o que se configura é o Power BI.
      </>
    ),
    recurso: RESOURCE_CATALOGO,
  },
  {
    id: 'clientes',
    rotulo: 'Clientes',
    titulo: 'Clientes',
    grupo: 'Cadastros',
    icone: 'people',
    descricao: (
      <>
        Cada cliente espelha um <strong>Tenant</strong> do Auth. Relatórios, datasets e políticas
        pertencem a um cliente. Nome e slug vêm do Auth — use ressincronizar para repuxá-los.
      </>
    ),
    recurso: RESOURCE_CATALOGO,
  },
  {
    id: 'hospitais',
    rotulo: 'Hospitais',
    titulo: 'Hospitais / Organizations',
    grupo: 'Cadastros',
    icone: 'layers',
    descricao: (
      <>
        Espelham <strong>Organizations</strong> do Auth (hospital, plano ou área). Servem para
        agrupar e para filtros por organização. Nome e slug vêm do Auth — use ressincronizar.
      </>
    ),
    recurso: RESOURCE_CATALOGO,
  },
  {
    id: 'conexoes',
    rotulo: 'Conexões Power BI',
    titulo: 'Conexões Power BI',
    grupo: 'Cadastros',
    icone: 'link',
    descricao: (
      <>
        Credenciais do <strong>service principal (Entra)</strong> usadas para falar com o Power BI.
        O segredo real fica no ambiente do backend; aqui guarda-se apenas a referência a ele.
      </>
    ),
    recurso: RESOURCE_CONEXOES,
  },
  {
    id: 'datasets',
    rotulo: 'Datasets',
    titulo: 'Datasets',
    grupo: 'Cadastros',
    icone: 'database',
    descricao:
      'Semantic models do Power BI: dados e roles de RLS. Um relatório aponta para um dataset, e é o dataset que o token de embed carrega.',
    recurso: RESOURCE_CATALOGO,
  },
  {
    id: 'relatorios',
    rotulo: 'Relatórios',
    titulo: 'Relatórios',
    grupo: 'Cadastros',
    icone: 'report',
    descricao: (
      <>
        Cada relatório está ligado a <strong>um workspace do Auth</strong>, que é a porta de acesso:
        só quem alcança esse workspace pelo profile o vê. Para criar, use o onboarding — aqui se
        consulta, edita e exclui.
      </>
    ),
    recurso: RESOURCE_CATALOGO,
  },
  {
    id: 'politicas',
    rotulo: 'Políticas de RLS',
    titulo: 'Políticas de RLS',
    grupo: 'Acesso',
    icone: 'shield',
    descricao: (
      <>
        Cada política liga <strong>relatório + profile + role(s)</strong> do semantic model. Sem
        política, o relatório fica em modo aberto: quem alcança o workspace vê todas as linhas.
      </>
    ),
    recurso: RESOURCE_POLICIES,
  },
  {
    id: 'diagnostico',
    rotulo: 'Diagnóstico do Auth',
    titulo: 'Diagnóstico do Auth',
    grupo: 'Diagnóstico',
    icone: 'processor',
    descricao: (
      <>
        Rotas publicadas por cada serviço do Auth, lidas do <code>openapi.json</code> de quem está
        no ar. Útil quando uma integração começa a falhar: se a rota sumiu ou mudou de forma, é aqui
        que aparece.
      </>
    ),
    recurso: RESOURCE_DIAGNOSTICO,
  },
]

/** Ordem dos grupos na navegacao - a mesma do menu do Streamlit, que veio do Auth Manager. */
const GRUPOS = ['Visão geral', 'Cadastros', 'Acesso', 'Diagnóstico']

function paginaDaUrl(): string {
  const pedida = new URLSearchParams(window.location.search).get('page') ?? 'home'
  return SECOES.some((item) => item.id === pedida) ? pedida : 'home'
}

export function App() {
  const [pagina, setPagina] = useState<string>(() => paginaDaUrl())
  /** Clicar na secao em que ja se esta volta para a colecao, fechando o registro aberto. */
  const [visita, setVisita] = useState(0)

  // Antes da primeira pintura: sem isto o login piscava no escuro antes de virar claro.
  useLayoutEffect(() => {
    aplicarTema(lerTema())
  }, [])

  useEffect(() => {
    function aoNavegar() {
      setPagina(paginaDaUrl())
    }
    window.addEventListener('popstate', aoNavegar)
    return () => window.removeEventListener('popstate', aoNavegar)
  }, [])

  return (
    <AuthGate>
      <Interno
        pagina={pagina}
        visita={visita}
        onNavegar={(id) => {
          if (id !== pagina) {
            window.history.pushState({}, '', id === 'home' ? window.location.pathname : `?page=${id}`)
            setPagina(id)
          }
          setVisita((atual) => atual + 1)
        }}
      />
    </AuthGate>
  )
}

/**
 * Uma secao por vez, sem montar as outras: cada tela dispara as proprias consultas, e manter as
 * demais montadas faria o app conversar com o Auth por telas que ninguem esta olhando.
 */
function Pagina({ id }: { id: string }) {
  switch (id) {
    case 'diagnostico':
      return <AuthCatalogPage />
    case 'onboarding':
      return <OnboardingPage />
    case 'clientes':
      return <ClientesPage />
    case 'hospitais':
      return <HospitaisPage />
    case 'conexoes':
      return <ConexoesPage />
    case 'datasets':
      return <DatasetsPage />
    case 'relatorios':
      return <RelatoriosPage />
    case 'politicas':
      return <PoliticasPage />
    default:
      return <HomePage />
  }
}

/** Com um registro aberto, o titulo da pagina e ele - ver `shell/registro.tsx`. */
function CabecalhoDaSecao({ secao }: { secao: Secao }) {
  if (useRegistroAberto()) return null
  return <PageHeader descricao={secao.descricao} sobretitulo={secao.grupo} titulo={secao.titulo} />
}

function Interno({
  pagina,
  visita,
  onNavegar,
}: {
  pagina: string
  visita: number
  onNavegar: (id: string) => void
}) {
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false })
  const permissoes = usePermissoes()
  const atual = SECOES.find((item) => item.id === pagina) ?? (SECOES[0] as Secao)

  /**
   * Enquanto a consulta de permissao nao volta, a navegacao mostra tudo. Esconder primeiro e revelar
   * depois faria o menu piscar a cada entrada, e visibilidade aqui nao e controle: quem recusa de
   * verdade e a rota, que ja esta checando.
   */
  function visivel(secao: Secao): boolean {
    return permissoes.carregando || permissoes.pode(secao.recurso, 'read')
  }

  const nav = GRUPOS.map((grupo) => {
    const doGrupo = SECOES.filter((secao) => secao.grupo === grupo && visivel(secao))
    if (doGrupo.length === 0) return null
    return (
      <NavGroup key={grupo} titulo={grupo}>
        {doGrupo.map((secao) => (
          <NavLink
            atual={secao.id === pagina}
            icon={secao.icone}
            key={secao.id}
            label={secao.rotulo}
            onClick={() => onNavegar(secao.id)}
          />
        ))}
      </NavGroup>
    )
  })

  // O tenant e o ESCOPO, como no Auth Manager: tudo o que o catalogo mostra pertence a ele.
  const escopo =
    me.data === undefined ? undefined : (
      <div className="scope-control__campo">
        <span>Tenant ativo</span>
        <strong>{me.data.tenant_name ?? me.data.tenant_id}</strong>
      </div>
    )

  return (
    <AppShell nav={nav} scope={escopo}>
      <ProvedorDeRegistro key={`${pagina}-${visita}`}>
        <CabecalhoDaSecao secao={atual} />
        <Pagina id={pagina} />
      </ProvedorDeRegistro>
    </AppShell>
  )
}
